import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button, Container, Nav, Navbar, Spinner,
} from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import dorianLogo from '../../../assets/images/dorian_logo_text_horizontal.svg';
import { auth } from '../../Auth/Auth';
import { PageWrapper } from '../../ui/PageWrapper';
import styles from './LatestUpdates.module.scss';

interface EngineUpdatesProps {
  history: {
    push: (path: string) => void;
  };
  match: {
    params: any;
  };
  location: {
    pathname: string;
  };
}

/*
* Used to load the markdown file from the public folder
*/
const MARKDOWN_FILE = '/latest-updates.md';

export function LatestUpdates(props: EngineUpdatesProps): JSX.Element {
  const user = auth.getUser();
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMarkdown = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(MARKDOWN_FILE);
        if (!response.ok) {
          throw new Error('Failed to load engine updates');
        }
        const content = await response.text();
        setMarkdownContent(content);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <Container fluid className={styles.engineUpdatesContainer}>
          <div className={styles.markdownContent}>
            <Alert variant="info" className={styles.loadingAlert}>
              <Spinner animation="border" role="status" size="sm" />
              <span>Loading engine updates...</span>
            </Alert>
          </div>
        </Container>
      );
    }

    if (error) {
      return (
        <Container fluid className={styles.engineUpdatesContainer}>
          <div className={styles.markdownContent}>
            <Alert variant="danger" className={styles.loadingAlert}>
              <span>
                Error loading latest updates:
                {error}
              </span>
            </Alert>
          </div>
        </Container>
      );
    }

    return (
      <Container fluid className={styles.engineUpdatesContainer}>
        <div className={styles.markdownContent}>
          <ReactMarkdown
            rehypePlugins={[rehypeSanitize as any]}
            remarkPlugins={[remarkGfm as any]}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </Container>
    );
  };

  const renderUnauthenticatedPage = () => (
    <div className={styles.engineUpdatesPage}>
      <Navbar expand="lg" className={styles.pageHeader}>
        <Container>
          <Navbar.Brand href="/">
            <div className={styles.logoContainer}>
              <img
                src={dorianLogo}
                alt="Dorian"
                className={styles.dorianLogo}
              />
            </div>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ml-auto">
              <Button
                as={Link}
                to="/"
                variant="asLinkV2"
              >
                Home
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <Container className={`mt-4 ${styles.container}`}>
        {renderContent()}
      </Container>
    </div>
  );

  // Check if user is authenticated
  if (!user) {
    return renderUnauthenticatedPage();
  }

  const page = {
    header: {
      title: 'Latest Updates',
      type: 'latest-updates',
      settings: user && user.role === 'admin' ? 'admin' : null,
      mainContentStyle: 'pageWrapperContentWithPadding',
    },
    sidebar: {
      nav: [
        {
          title: 'Back to Home',
          href: '/',
          variant: 'secondary',
          disabled: false,
        },
      ],
    },
  };

  return (
    <PageWrapper
      {...props}
      page={page}
    >
      {renderContent()}
    </PageWrapper>
  );
}
